/**
 * Per-user card style presets.
 * Each preset defines visual properties applied to chart/analytics cards.
 * The active preset key is stored in localStorage via SettingsContext.
 */

export const CARD_STYLE_PRESETS = {
  clean: {
    key: "clean",
    label: "cardStyles.clean",
    description: "cardStyles.cleanDescription",
    card: {
      backgroundColor: "transparent",
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "8px",
      boxShadow: "none",
      backdropFilter: "none",
    },
    chartArea: {
      backgroundColor: "transparent",
    },
    dataEmphasis: "standard",
  },
  elevated: {
    key: "elevated",
    label: "cardStyles.elevated",
    description: "cardStyles.elevatedDescription",
    card: {
      backgroundColor: {
        light: "rgba(255, 255, 255, 0.95)",
        dark: "rgba(30, 30, 30, 0.95)",
      },
      borderWidth: "0px",
      borderStyle: "none",
      borderRadius: "12px",
      boxShadow: {
        light: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
        dark: "0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
      },
      backdropFilter: "none",
    },
    chartArea: {
      backgroundColor: {
        light: "rgba(249, 250, 251, 0.5)",
        dark: "rgba(40, 40, 40, 0.5)",
      },
    },
    dataEmphasis: "standard",
  },
  glass: {
    key: "glass",
    label: "cardStyles.glass",
    description: "cardStyles.glassDescription",
    card: {
      backgroundColor: {
        light: "rgba(255, 255, 255, 0.45)",
        dark: "rgba(40, 40, 40, 0.35)",
      },
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "16px",
      boxShadow: {
        light: "0 4px 30px rgba(0, 0, 0, 0.05)",
        dark: "0 4px 30px rgba(0, 0, 0, 0.2)",
      },
      backdropFilter: "blur(12px) saturate(150%)",
    },
    chartArea: {
      backgroundColor: "transparent",
    },
    dataEmphasis: "standard",
  },
  bold: {
    key: "bold",
    label: "cardStyles.bold",
    description: "cardStyles.boldDescription",
    card: {
      backgroundColor: {
        light: "#ffffff",
        dark: "rgba(25, 25, 25, 1)",
      },
      borderWidth: "0px",
      borderStyle: "none",
      borderRadius: "12px",
      boxShadow: {
        light: "0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
        dark: "0 2px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
      },
      backdropFilter: "none",
    },
    chartArea: {
      backgroundColor: {
        light: "rgba(245, 247, 250, 0.8)",
        dark: "rgba(35, 35, 35, 0.8)",
      },
    },
    dataEmphasis: "high",
  },
  softGradient: {
    key: "softGradient",
    label: "cardStyles.softGradient",
    description: "cardStyles.softGradientDescription",
    card: {
      backgroundColor: {
        light:
          "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(245,248,255,0.9) 100%)",
        dark: "linear-gradient(135deg, rgba(30,30,35,0.9) 0%, rgba(25,28,40,0.9) 100%)",
      },
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "16px",
      boxShadow: {
        light: "0 2px 12px rgba(0,0,0,0.04)",
        dark: "0 2px 12px rgba(0,0,0,0.3)",
      },
      backdropFilter: "blur(6px)",
    },
    chartArea: {
      backgroundColor: "transparent",
    },
    dataEmphasis: "standard",
  },
  darkExecutive: {
    key: "darkExecutive",
    label: "cardStyles.darkExecutive",
    description: "cardStyles.darkExecutiveDescription",
    card: {
      backgroundColor: {
        light: "rgba(15, 23, 42, 0.95)",
        dark: "rgba(10, 10, 15, 0.95)",
      },
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "12px",
      boxShadow: {
        light: "0 4px 20px rgba(0,0,0,0.15)",
        dark: "0 4px 20px rgba(0,0,0,0.4)",
      },
      backdropFilter: "none",
      // Forces light text on dark card background
      forceDarkText: true,
    },
    chartArea: {
      backgroundColor: "rgba(0,0,0,0.15)",
    },
    dataEmphasis: "glow",
  },
};

/**
 * Resolve mode-specific values from a preset property.
 * If the value is an object with light/dark keys, returns the correct one.
 * If it's a string, returns as-is.
 */
export function resolveStyleValue(value, mode) {
  if (
    value &&
    typeof value === "object" &&
    ("light" in value || "dark" in value)
  ) {
    return value[mode] || value.light || value.dark;
  }
  return value;
}

/**
 * Get CSS custom properties for the active card style preset.
 * Applied to :root so all cards pick them up via theme overrides.
 */
export function getCardStyleCSSVars(presetKey, mode) {
  const preset = CARD_STYLE_PRESETS[presetKey] || CARD_STYLE_PRESETS.clean;
  const bg = resolveStyleValue(preset.card.backgroundColor, mode);
  const isGradient = typeof bg === "string" && bg.includes("gradient");

  return {
    "--card-bg": isGradient ? "transparent" : bg,
    "--card-bg-image": isGradient ? bg : "none",
    "--card-border-width": preset.card.borderWidth,
    "--card-border-style": preset.card.borderStyle,
    "--card-border-radius": preset.card.borderRadius,
    "--card-box-shadow":
      resolveStyleValue(preset.card.boxShadow, mode) || "none",
    "--card-backdrop-filter": preset.card.backdropFilter || "none",
    "--card-chart-area-bg":
      resolveStyleValue(preset.chartArea.backgroundColor, mode) ||
      "transparent",
    "--card-data-emphasis": preset.dataEmphasis || "standard",
  };
}
